import { useState, useMemo } from 'react'
import {
  useDashboardStats,
  useRevenueMetrics,
  usePipelineByStage,
  useDaysToClose,
} from '@/hooks/use-api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from 'recharts'
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  Loader2,
  Filter,
  Calendar,
  Clock,
} from 'lucide-react'
import {
  formatCurrency,
  formatPercent,
  getFiscalYearRange,
  formatDate,
  toDisplayDate,
} from '@/lib/utils'
import { CHART_COLORS, CHART_SERIES, GRACKLE_COLORS, CATEGORICAL, SEMANTIC } from '@/lib/theme'
import { useNavigate } from 'react-router-dom'
import { SalesForecastChart } from '@/components/SalesForecastChart'
import { ChartTooltipContent } from '@/components/ui/chart-tooltip'

const COLORS = CHART_COLORS

// Compact currency formatter for chart labels (e.g. "$2.1M", "$769K")
function compactCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `$${Math.round(value / 1_000)}K`
  return `$${value}`
}

// GRK-BRAND-4: funnel stages use the on-brand extended chart series.
const FUNNEL_COLORS = CHART_SERIES

function toDateString(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function getPresetRange(preset: string): { startDate: string; endDate: string } {
  const today = new Date()
  const end = toDateString(today)
  let start: Date

  switch (preset) {
    case '30d':
      start = new Date(today)
      start.setDate(start.getDate() - 30)
      break
    case 'quarter': {
      start = new Date(today)
      start.setMonth(start.getMonth() - 3)
      break
    }
    case 'ytd':
      // GRM's fiscal year runs Mar 1 → end of Feb. "Year to Date" means
      // fiscal-YTD, not calendar-YTD — see getFiscalYearRange() in lib/utils.
      return getFiscalYearRange(today)
    case '12m':
    default:
      start = new Date(today)
      start.setFullYear(start.getFullYear() - 1)
      break
  }

  return { startDate: toDateString(start), endDate: end }
}

export default function Analytics() {
  const navigate = useNavigate()

  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' })
  const [appliedFilters, setAppliedFilters] = useState<{ startDate?: string; endDate?: string }>({})

  const { data: stats } = useDashboardStats(appliedFilters)
  const { data: revenueData, isLoading: revenueLoading } = useRevenueMetrics({
    groupBy: 'month',
    ...appliedFilters,
  })
  const { data: pipelineData, isLoading: pipelineLoading } = usePipelineByStage(appliedFilters)
  const { data: daysToCloseData, isLoading: daysToCloseLoading } = useDaysToClose(appliedFilters)

  const handleApplyFilters = () => {
    const filters: { startDate?: string; endDate?: string } = {}
    if (dateRange.startDate) filters.startDate = dateRange.startDate
    if (dateRange.endDate) filters.endDate = dateRange.endDate
    setAppliedFilters(filters)
  }

  const handleClearFilters = () => {
    setDateRange({ startDate: '', endDate: '' })
    setAppliedFilters({})
  }

  const handlePreset = (preset: string) => {
    const range = getPresetRange(preset)
    setDateRange(range)
    setAppliedFilters(range)
  }

  const hasFilters = !!(appliedFilters.startDate || appliedFilters.endDate)

  // Transform revenue data for charts
  const chartData = useMemo(() => {
    if (!revenueData) return []
    return revenueData.map((item) => ({
      month: (toDisplayDate(item.period) ?? new Date(item.period)).toLocaleDateString('en-US', {
        month: 'short',
      }),
      revenue: Number(item.revenue),
      opportunities: item.won_opportunities,
      total_opportunities: item.total_opportunities,
      win_rate: item.win_rate,
      avg_deal_size: Number(item.avg_deal_size),
    }))
  }, [revenueData])

  // Transform pipeline data for charts
  const pipelineChartData = useMemo(() => {
    if (!pipelineData) return []
    return pipelineData.map((item) => ({
      stage: item.stage_name,
      count: item.opportunity_count,
      value: Number(item.total_value),
      weighted_value: Number(item.weighted_value),
      avg_probability: Number(item.avg_probability),
    }))
  }, [pipelineData])

  // Pipeline funnel data — open stages only, sorted by value descending for funnel shape
  const funnelData = useMemo(() => {
    const closedStages = ['Closed Won', 'Closed Lost', 'Service (Do not Use)']
    return pipelineChartData
      .filter((item) => !closedStages.includes(item.stage) && item.value > 0)
      .sort((a, b) => b.value - a.value)
  }, [pipelineChartData])

  // Transform days-to-close trends for chart
  const daysToCloseChartData = useMemo(() => {
    if (!daysToCloseData?.trends) return []
    return daysToCloseData.trends.map((item: any) => ({
      month: (toDisplayDate(item.month) ?? new Date(item.month)).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      }),
      avgDays: parseFloat(Number(item.avg_days).toFixed(1)),
      closedCount: item.closed_count,
    }))
  }, [daysToCloseData])

  // Calculate aggregate metrics from real data
  const metrics = useMemo(() => {
    if (!revenueData || revenueData.length === 0) return null

    // Find the latest month with actual closed deals (not empty future months)
    const latestMonthWithData = revenueData
      .slice()
      .reverse()
      .find((month) => month.won_opportunities > 0 || month.lost_opportunities > 0)
    if (!latestMonthWithData) return null

    const latestMonth = latestMonthWithData
    const latestIndex = revenueData.indexOf(latestMonth)
    const previousMonth = latestIndex > 0 ? revenueData[latestIndex - 1] : null

    const avgDealSize = Number(latestMonth.avg_deal_size)
    const winRate = latestMonth.win_rate
    const prevAvgDealSize = previousMonth ? Number(previousMonth.avg_deal_size) : avgDealSize
    const prevWinRate = previousMonth ? previousMonth.win_rate : winRate

    return {
      avgDealSize,
      winRate,
      dealSizeChange:
        prevAvgDealSize !== 0 ? ((avgDealSize - prevAvgDealSize) / prevAvgDealSize) * 100 : 0,
      winRateChange: prevWinRate !== 0 ? winRate - prevWinRate : 0,
    }
  }, [revenueData])

  const hasEmptyRevenue = !revenueLoading && (!revenueData || revenueData.length === 0)

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Analytics</h1>
          <p className="text-muted-foreground">Advanced analytics and insights for your CRM data</p>
        </div>
        <Button
          onClick={() => navigate('/analytics/lead-sources')}
          variant="outline"
          aria-label="View detailed lead source analysis"
        >
          <TrendingUp className="mr-2 h-4 w-4" aria-hidden="true" />
          Lead Source Analysis
        </Button>
      </div>

      {/* Date Range Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" aria-hidden="true" />
            Date Range
          </CardTitle>
          <CardDescription>
            Filter revenue and trend data by date range (pipeline data is unaffected)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-4">
            <div>
              <Label htmlFor="analyticsStartDate">Start Date</Label>
              <DatePicker
                id="analyticsStartDate"
                value={dateRange.startDate}
                onChange={(v) => setDateRange((prev) => ({ ...prev, startDate: v ?? '' }))}
                aria-label="Start date for filtering analytics"
              />
            </div>
            <div>
              <Label htmlFor="analyticsEndDate">End Date</Label>
              <DatePicker
                id="analyticsEndDate"
                value={dateRange.endDate}
                onChange={(v) => setDateRange((prev) => ({ ...prev, endDate: v ?? '' }))}
                aria-label="End date for filtering analytics"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button
                onClick={handleApplyFilters}
                className="flex-1"
                aria-label="Apply date range filter"
              >
                Apply
              </Button>
              {hasFilters && (
                <Button
                  onClick={handleClearFilters}
                  variant="outline"
                  aria-label="Clear date range filter"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="flex items-center text-sm text-muted-foreground mr-1">
              <Calendar className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
              Quick:
            </span>
            <Button variant="outline" size="sm" onClick={() => handlePreset('30d')}>
              Last 30 Days
            </Button>
            <Button variant="outline" size="sm" onClick={() => handlePreset('quarter')}>
              Last Quarter
            </Button>
            <Button variant="outline" size="sm" onClick={() => handlePreset('ytd')}>
              Year to Date
            </Button>
            <Button variant="outline" size="sm" onClick={() => handlePreset('12m')}>
              Last 12 Months
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Empty state warning when filters return no data */}
      {hasFilters && hasEmptyRevenue && (
        <Card
          style={{
            borderColor: SEMANTIC.warning.fill,
            backgroundColor: SEMANTIC.warning.fill,
          }}
        >
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="font-medium" style={{ color: SEMANTIC.warning.text }}>
                No revenue data found for the selected date range
              </p>
              <p className="text-sm" style={{ color: SEMANTIC.warning.text }}>
                {appliedFilters.startDate && appliedFilters.endDate ? (
                  <>
                    Your filter from {formatDate(appliedFilters.startDate)} to{' '}
                    {formatDate(appliedFilters.endDate)} returned no closed opportunities. Try
                    adjusting your date range or clearing the filters.
                  </>
                ) : (
                  'No revenue data available for this range. Try clearing the filters.'
                )}
              </p>
              <Button
                onClick={handleClearFilters}
                variant="outline"
                size="sm"
                className="mt-2"
                aria-label="Clear date range filters"
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Metrics */}
      <div className="grid gap-2 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
            <TrendingUp
              className="h-4 w-4"
              style={{ color: CATEGORICAL.evergreen.solid }}
              aria-hidden="true"
            />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              {formatPercent(stats?.conversion_rate ?? 0)}
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1 leading-tight">
              <TrendingUp
                className="h-3 w-3"
                style={{ color: CATEGORICAL.evergreen.solid }}
                aria-hidden="true"
              />
              <span>12% from last month</span>
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Win Rate</CardTitle>
            <Target
              className="h-4 w-4"
              style={{ color: CATEGORICAL.navy.solid }}
              aria-hidden="true"
            />
          </CardHeader>
          <CardContent className="p-3">
            {revenueLoading ? (
              <Loader2
                className="h-6 w-6 animate-spin text-muted-foreground"
                aria-label="Loading win rate"
              />
            ) : (
              <>
                <div className="text-xl font-bold leading-none">
                  {formatPercent(metrics?.winRate ?? 0)}
                </div>
                <p className="text-xs text-muted-foreground flex items-center gap-1 leading-tight">
                  {metrics && metrics.winRateChange !== 0 ? (
                    <>
                      {metrics.winRateChange > 0 ? (
                        <TrendingUp
                          className="h-3 w-3"
                          style={{ color: CATEGORICAL.evergreen.solid }}
                          aria-hidden="true"
                        />
                      ) : (
                        <TrendingDown
                          className="h-3 w-3"
                          style={{ color: GRACKLE_COLORS.darkRaspberry }}
                          aria-hidden="true"
                        />
                      )}
                      <span>{Math.abs(metrics.winRateChange).toFixed(1)}% from last month</span>
                    </>
                  ) : (
                    <span>No change from last month</span>
                  )}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Avg Deal Size</CardTitle>
            <Activity
              className="h-4 w-4"
              style={{ color: CATEGORICAL.plum.solid }}
              aria-hidden="true"
            />
          </CardHeader>
          <CardContent className="p-3">
            {revenueLoading ? (
              <Loader2
                className="h-6 w-6 animate-spin text-muted-foreground"
                aria-label="Loading average deal size"
              />
            ) : (
              <>
                <div className="text-xl font-bold leading-none">
                  {formatCurrency(metrics?.avgDealSize ?? 0, 'CAD')}
                </div>
                <p className="text-xs text-muted-foreground flex items-center gap-1 leading-tight">
                  {metrics && metrics.dealSizeChange !== 0 ? (
                    <>
                      {metrics.dealSizeChange > 0 ? (
                        <TrendingUp
                          className="h-3 w-3"
                          style={{ color: CATEGORICAL.evergreen.solid }}
                          aria-hidden="true"
                        />
                      ) : (
                        <TrendingDown
                          className="h-3 w-3"
                          style={{ color: GRACKLE_COLORS.darkRaspberry }}
                          aria-hidden="true"
                        />
                      )}
                      <span>{Math.abs(metrics.dealSizeChange).toFixed(1)}% from last month</span>
                    </>
                  ) : (
                    <span>No change from last month</span>
                  )}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Pipeline Value</CardTitle>
            <Activity
              className="h-4 w-4"
              style={{ color: CATEGORICAL.clay.solid }}
              aria-hidden="true"
            />
          </CardHeader>
          <CardContent className="p-3">
            {pipelineLoading ? (
              <Loader2
                className="h-6 w-6 animate-spin text-muted-foreground"
                aria-label="Loading pipeline value"
              />
            ) : (
              <>
                <div className="text-xl font-bold leading-none">
                  {formatCurrency(pipelineChartData.reduce((sum, item) => sum + item.value, 0))}
                </div>
                <p className="text-xs text-muted-foreground flex items-center gap-1 leading-tight">
                  <span>
                    {pipelineChartData.reduce((sum, item) => sum + item.count, 0)} open
                    opportunities
                  </span>
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sales Forecast */}
      <SalesForecastChart months={12} forecastPeriod={3} />

      {/* Pipeline Analysis */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Pipeline</CardTitle>
                <CardDescription>How much business is in our pipeline?</CardDescription>
              </div>
              {!pipelineLoading && funnelData.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  Sum of Amount:{' '}
                  {compactCurrency(funnelData.reduce((sum, item) => sum + item.value, 0))}
                </p>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {pipelineLoading ? (
              <div className="flex items-center justify-center h-[400px]">
                <Loader2
                  className="h-8 w-8 animate-spin text-muted-foreground"
                  aria-label="Loading pipeline data"
                />
              </div>
            ) : funnelData.length === 0 ? (
              <div className="flex items-center justify-center h-[400px] text-muted-foreground">
                No pipeline data available
              </div>
            ) : (
              <div
                role="img"
                aria-label="Pipeline funnel chart showing opportunities by stage"
                className="flex gap-4"
              >
                {/* Funnel SVG */}
                <svg viewBox="0 0 400 360" className="flex-1" style={{ maxHeight: 400 }}>
                  {funnelData.map((item, i) => {
                    const n = funnelData.length
                    const segH = 340 / n
                    const gap = 2
                    const y = 10 + i * segH
                    // Taper linearly from full width to ~30%
                    const topW = 400 * (1 - (i * 0.7) / n)
                    const botW = 400 * (1 - ((i + 1) * 0.7) / n)
                    const topX = (400 - topW) / 2
                    const botX = (400 - botW) / 2
                    const points = `${topX},${y + gap} ${topX + topW},${y + gap} ${botX + botW},${y + segH - gap} ${botX},${y + segH - gap}`
                    const cy = y + segH / 2
                    return (
                      <g key={item.stage}>
                        <polygon
                          points={points}
                          fill={FUNNEL_COLORS[i % FUNNEL_COLORS.length]}
                          className="cursor-pointer transition-opacity hover:opacity-80"
                        >
                          <title>{`${item.stage}: ${formatCurrency(item.value, 'CAD')} (${item.count} opportunities)`}</title>
                        </polygon>
                        <text
                          x={200}
                          y={cy}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fill="#fff"
                          fontWeight="bold"
                          fontSize={segH > 35 ? 16 : 13}
                        >
                          {compactCurrency(item.value)}
                        </text>
                      </g>
                    )
                  })}
                </svg>
                {/* Legend */}
                <div
                  className="flex flex-col justify-center gap-2 pr-2 min-w-[140px]"
                  aria-label="Stage legend"
                >
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Stage</p>
                  {funnelData.map((item, i) => (
                    <div key={item.stage} className="flex items-center gap-2 text-sm">
                      <span className="text-right flex-1 truncate">{item.stage}</span>
                      <span
                        className="inline-block h-3.5 w-3.5 rounded-sm flex-shrink-0"
                        style={{ backgroundColor: FUNNEL_COLORS[i % FUNNEL_COLORS.length] }}
                        aria-hidden="true"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Win Rate & Opportunity Trend</CardTitle>
            <CardDescription>
              Win Rate = Won / (Won + Lost) %. Won Opportunities = deals closed as won per month
              {hasFilters ? ' (filtered)' : ''}.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {revenueLoading ? (
              <div className="flex items-center justify-center h-[300px]">
                <Loader2
                  className="h-8 w-8 animate-spin text-muted-foreground"
                  aria-label="Loading trend data"
                />
              </div>
            ) : chartData.length === 0 ? (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                No trend data available
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip
                    content={<ChartTooltipContent />}
                    formatter={(value: number, name: string) => {
                      if (name === 'Win Rate') return `${value}%`
                      return value
                    }}
                  />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="opportunities"
                    stroke={COLORS[1]}
                    strokeWidth={2}
                    name="Won Opportunities"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="win_rate"
                    stroke={COLORS[2]}
                    strokeWidth={2}
                    name="Win Rate"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Days to Close */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" aria-hidden="true" />
                Days to Close
              </CardTitle>
              <CardDescription>How long does it take to close a deal?</CardDescription>
            </div>
            {daysToCloseData?.overall && (
              <div className="text-right">
                <div className="text-2xl font-bold">
                  {parseFloat(Number(daysToCloseData.overall.avg_days_to_close).toFixed(1))}
                </div>
                <p className="text-xs text-muted-foreground">avg days overall</p>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {daysToCloseLoading ? (
            <div className="flex items-center justify-center h-[350px]">
              <Loader2
                className="h-8 w-8 animate-spin text-muted-foreground"
                aria-label="Loading days to close data"
              />
            </div>
          ) : daysToCloseChartData.length === 0 ? (
            <div className="flex items-center justify-center h-[350px] text-muted-foreground">
              No days-to-close data available
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart
                data={daysToCloseChartData}
                margin={{ top: 20, right: 30, left: 10, bottom: 60 }}
              >
                <defs>
                  <linearGradient id="colorDaysToClose" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={GRACKLE_COLORS.navy} stopOpacity={0.1} />
                    <stop offset="95%" stopColor={GRACKLE_COLORS.navy} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="month"
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fontSize: 12 }}
                  label={{
                    value: 'Close Month',
                    position: 'insideBottom',
                    offset: -5,
                    style: { fontSize: 12, fill: 'hsl(var(--muted-foreground))' },
                  }}
                />
                <YAxis
                  label={{
                    value: 'Average Days',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 12, fill: 'hsl(var(--muted-foreground))' },
                  }}
                />
                <Tooltip
                  content={<ChartTooltipContent />}
                  formatter={(value: number) => [`${value} days`, 'Avg Days to Close']}
                />
                <Line
                  type="monotone"
                  dataKey="avgDays"
                  stroke={GRACKLE_COLORS.navy}
                  strokeWidth={2}
                  dot={{ r: 4, fill: GRACKLE_COLORS.navy, stroke: GRACKLE_COLORS.navy }}
                  activeDot={{ r: 6 }}
                  name="Avg Days to Close"
                >
                  <LabelList
                    dataKey="avgDays"
                    position="top"
                    offset={10}
                    style={{ fontSize: 11, fill: 'hsl(var(--foreground))' }}
                  />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
